// client/components/ui/ResponsiveDataView.jsx
import React from "react";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useIsMobile } from "./useIsMobile.js";
import { mobileOverflowGuardClassName } from "../../design/tokens.js";

/**
 * ResponsiveDataView - Responsive Table/CardList Komponente
 * 
 * Desktop (>=md): Rendert Table-Layout
 * Mobile (<md): Rendert CardList mit Actions
 * 
 * Props:
 * - data: Array<any> - Daten-Array
 * - columns: Array<{key: string, label: string, render?: (row) => ReactNode, align?: "left"|"right"|"center"}>
 * - renderCard?: (row) => ReactNode - Custom Card-Renderer (optional)
 * - onRowClick?: (row) => void - Click-Handler für Rows/Cards
 * - emptyMessage?: string - Nachricht wenn keine Daten
 * - loading?: boolean
 * - className?: string
 */
export default function ResponsiveDataView({
  data = [],
  columns = [],
  renderCard,
  onRowClick,
  emptyMessage = "Keine Einträge gefunden.",
  loading = false,
  className = ""
}) {
    const isMobile = useIsMobile();
  const cardCls = "interactive-card rounded-2xl bg-surface border border-default hover:border-blue-400/40 transition-all";
  const cellPad = "px-3 py-2";
  const headTxt = "text-xs";
  const bodyTxt = "text-sm";

  // Loading State
  if (loading) {
    return <div className={`space-y-4 ${className}`}>
        {isMobile ? <>
            <div className={`${cardCls} h-32 animate-pulse`} />
            <div className={`${cardCls} h-32 animate-pulse`} />
            <div className={`${cardCls} h-32 animate-pulse`} />
          </> : <div className={`rounded-2xl border border-default overflow-hidden bg-surface ${""}`}>
            <div className="space-y-2 p-4">
              <div className={`h-10 rounded animate-pulse ${"bg-muted"}`} />
              <div className={`h-10 rounded animate-pulse ${"bg-muted"}`} />
              <div className={`h-10 rounded animate-pulse ${"bg-muted"}`} />
            </div>
          </div>}
      </div>;
  }

  // Empty State
  if (data.length === 0) {
    return <div className={`${className}`}>
        <div className={`${cardCls} px-6 py-10 text-center text-muted`}>
          {emptyMessage}
        </div>
      </div>;
  }

  // Mobile: CardList
  if (isMobile) {
    return <div className={`space-y-3 min-w-0 ${mobileOverflowGuardClassName} ${className}`}>
        {data.map((row, idx) => {
        // Custom Card Renderer
        if (renderCard) {
          return (
            <div
              key={row.id || idx}
              className="min-w-0 cursor-pointer"
              onClick={() => onRowClick?.(row)}
              onKeyDown={(e) => {
                if (!onRowClick) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(row);
                }
              }}
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {renderCard(row)}
            </div>
          );
        }

        // Default Card Layout
        return <div key={row.id || idx} onClick={() => onRowClick?.(row)} className={`${cardCls} p-4 min-w-0 cursor-pointer active:scale-[0.99] transition-transform`}>
              <div className="space-y-2 min-w-0">
                {columns.map(col => {
              const value = row[col.key];
              const content = col.render ? col.render(row) : value ?? "—";
              return <div key={col.key} className="flex items-start justify-between gap-2 min-w-0">
                      <span className="text-xs font-medium shrink-0 text-muted max-w-[40%]">
                        {col.label}:
                      </span>
                      <div className={`flex-1 min-w-0 break-words ${bodyTxt} ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} text-primary`}>
                        {content}
                      </div>
                    </div>;
            })}
              </div>
            </div>;
      })}
      </div>;
  }

  // Desktop: Table
  return <div className={`rounded-2xl border border-default overflow-hidden bg-surface ${""} ${className}`}>
      <div className="w-full overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-muted">
          <tr className="text-left text-muted uppercase tracking-wide">
            {columns.map(col => <th key={col.key} className={`${cellPad} ${headTxt} font-semibold ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"}`}>
                {col.label}
              </th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => <tr key={row.id || idx} className={`border-t border-default hover:bg-muted ${bodyTxt} ${onRowClick ? "cursor-pointer" : ""}`} onClick={() => onRowClick?.(row)}>
              {columns.map(col => {
              const value = row[col.key];
              const content = col.render ? col.render(row) : value ?? "—";
              return <td key={col.key} className={`${cellPad} max-w-[22rem] break-words ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} text-primary`}>
                    {content}
                  </td>;
            })}
            </tr>)}
        </tbody>
      </table>
      </div>
    </div>;
}